// Hợp đồng của performance-service (đặc tả docs/backend/services/performance-service.md): chỉ tiêu tháng, báo cáo
// doanh số & KPI (projection từ crm/deal — salesStats của packages/domain), số liệu Tổng quan, đầu vào thưởng KPI
// cho commission. Quy ước: tiền VND số nguyên (number); tháng YYYY-MM (Asia/Ho_Chi_Minh); id UUID.
import { z } from 'zod';
import type { MyToday, TodayPresence } from '../attendance/index.js';
import type { ProjectBoardSummary } from '../booking/index.js';
import type { MyCommissionSummary } from '../commission/index.js';
import type { Inbox } from '../notification/index.js';

// ---------- Bộ dựng schema (thông điệp tiếng Việt) ----------
const REQUIRED = 'Thiếu dữ liệu bắt buộc';
const str = (required = REQUIRED) => z.string({ required_error: required, invalid_type_error: 'Phải là chuỗi ký tự' });
const uuid = str().uuid('Mã định danh không hợp lệ');
const int = () => z.number({ required_error: REQUIRED, invalid_type_error: 'Phải là số' }).int('Phải là số nguyên');
const monthKeyReq = (required = 'Chọn tháng') => str(required).regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Tháng không hợp lệ');
const monthKey = monthKeyReq();
const qInt = (min: number, max: number, def?: number) => {
  const base = z.coerce.number({ invalid_type_error: 'Phải là số' }).int('Phải là số nguyên').min(min, `Tối thiểu ${min}`).max(max, `Tối đa ${max}`);
  return def === undefined ? base.optional() : base.default(def);
};

// ---------- Hằng số ----------
export const TARGET_SOURCES = ['monthly', 'default', 'none'] as const;
export type TargetSource = (typeof TARGET_SOURCES)[number];
export const KPI_CAUSES = ['deal', 'target', 'defaultTarget', 'reconcile'] as const;
export type KpiCause = (typeof KPI_CAUSES)[number];
export const SCOPE_KINDS = ['own', 'team', 'all', 'none'] as const;
export type ScopeKind = (typeof SCOPE_KINDS)[number];

// ---------- Lệnh ----------
/** amount = null ⇒ xoá giá trị tường minh (rơi về chỉ tiêu mặc định của hr — T2). Gộp: chỉ người trong lệnh bị đổi (T5). */
export const targetEntryInput = z.object({
  staffId: uuid,
  amount: z
    .number({ invalid_type_error: 'Chỉ tiêu phải là số tiền nguyên không âm' })
    .int('Chỉ tiêu phải là số tiền nguyên không âm')
    .min(0, 'Chỉ tiêu phải là số tiền nguyên không âm')
    .max(9e15, 'Chỉ tiêu vượt giới hạn')
    .nullable(),
});
export const setMonthlyTargetsInput = z.object({
  month: monthKey,
  entries: z
    .array(targetEntryInput, { required_error: 'Nhập chỉ tiêu', invalid_type_error: 'Danh sách chỉ tiêu không hợp lệ' })
    .min(1, 'Nhập chỉ tiêu cho ít nhất một người')
    .max(500, 'Tối đa 500 người mỗi lần')
    .refine((a) => new Set(a.map((e) => e.staffId)).size === a.length, 'Nhân sự bị trùng trong danh sách'),
  /** If-Match tuỳ chọn (T7). */
  expectedVersion: int().min(0, 'Phiên bản không hợp lệ').optional(),
});

// ---------- Truy vấn công khai ----------
/** Tháng mặc định = tháng hiện tại (VN); tháng tương lai ⇒ 422 REPORT_MONTH_IN_FUTURE. */
export const getSalesReportInput = z.object({ month: monthKey.optional() });
export const getMonthlyTargetsInput = z.object({ month: monthKey.optional() });
export const getDashboardSalesInput = z.object({ month: monthKey.optional() });
export const getTargetsSetupStatusInput = z.object({});

// ---------- Truy vấn nội bộ ----------
/** commission tính kỳ chi trả: gtgd (S11) + chỉ tiêu hiệu lực (T6), mặc định mọi nhân sự (gồm đã nghỉ). */
export const getKpiInputsInput = z.object({ month: monthKey, staffIds: z.array(uuid).max(5000, 'Tối đa 5.000 nhân sự').optional() });
export const listKpiInputsInput = z.object({ month: monthKey, afterStaffId: uuid.optional(), limit: qInt(1, 1000, 500) });
export const getStaffMonthStatsInput = z.object({ staffId: uuid, month: monthKey });

/** Tên RPC: performance.cmd.<Name> / performance.qry.<Name>. */
export const RPC = {
  cmd: ['SetMonthlyTargets'],
  qry: ['GetSalesReport', 'GetMonthlyTargets', 'GetDashboardSales', 'GetTargetsSetupStatus', 'GetKpiInputs', 'ListKpiInputs', 'GetStaffMonthStats'],
} as const;

// ---------- DTO / read model ----------
/** Chỉ số doanh số của một người × tháng (đúng salesStats). */
export interface SalesStats {
  leads: number;
  contacted: number;
  viewed: number;
  won: number;
  signed: number;
  slaMiss: number;
  viewings: number;
  bookings: number;
  deposits: number;
  contracts: number;
  gtgd: number;
  fee: number;
  cancels: number;
  /** leads ? won / leads × 100 : 0. */
  conversion: number;
}
export interface SalesReportRow extends SalesStats {
  staffId: string;
  name: string;
  code: string | null;
  teamId: string | null;
  teamName: string | null;
  roleId: string | null;
  target: number;
  targetSource: TargetSource;
  /** target ? gtgd / target × 100 : 0. */
  pct: number;
}
export interface SalesTotals extends SalesStats {
  target: number;
}
export interface ReportScope {
  kind: ScopeKind;
  size: number;
}
export interface SalesReport {
  month: string;
  serverTime: string;
  scope: ReportScope;
  rows: SalesReportRow[];
  totals: SalesTotals;
  prevMonthGtgd: number;
  deltaPct: number | null;
  byDay: { day: number; value: number }[];
  teams: { teamId: string; name: string; value: number; target: number }[];
  sources: { source: string; leads: number; won: number; deals: number; gtgd: number; conversion: number | null }[];
  actions: { canSetTargets: boolean };
}
export interface MonthlyTargetPerson {
  staffId: string;
  name: string;
  roleId: string | null;
  explicit: number | null;
  defaultTarget: number;
  /** explicit ?? defaultTarget ?? 0. */
  initial: number;
  /** Giá trị tường minh tháng trước ("Chép tháng trước"). */
  previousExplicit: number | null;
}
export interface MonthlyTargets {
  month: string;
  version: number;
  people: MonthlyTargetPerson[];
  totalInitial: number;
  kpiBonusTiers: { minPct: number; amount: number }[];
}
export interface SetMonthlyTargetsResult {
  version: number;
  /** Σ amount các dòng trong lệnh (null tính 0). */
  total: number;
  /** Σ mọi dòng tường minh của tháng. */
  sheetTotal: number;
}
export interface DashboardSales {
  month: string;
  serverTime: string;
  scope: ReportScope;
  totals: { gtgd: number; deposits: number; fee: number; leads: number; won: number; target: number; contracts: number };
  /** 6 tháng gần nhất (M−5 … M). */
  series: { month: string; value: number }[];
  /** Top 8 theo GTGD. */
  ranking: { staffId: string; name: string; value: number; target: number }[];
}
export interface TargetsSetupStatus {
  hasAnyTargets: boolean;
}
// Nội bộ
export interface KpiInputRow {
  staffId: string;
  gtgd: number;
  target: number;
  targetSource: TargetSource;
  revision: number;
}
export interface KpiInputs {
  month: string;
  computedAt: string;
  rows: KpiInputRow[];
  nextAfterStaffId?: string | null;
}
export interface StaffMonthStats extends SalesStats {
  staffId: string;
  month: string;
  target: number;
  targetSource: TargetSource;
}

// ---------- Tổng quan (BFF gateway GET /dashboard — gateway.md Q-BFF1) ----------
/** Mỗi khối gọi song song, độc lập: lỗi một khối không làm hỏng phản hồi; khối của module tắt ⇒ disabled. */
export type DashboardBlockStatus = 'ok' | 'unavailable' | 'disabled' | 'forbidden';
export interface DashboardBlock<T> {
  status: DashboardBlockStatus;
  data?: T;
}
export interface DashboardViewer {
  /** = có booking.lock (thay SELLER_ROLES). */
  isSeller: boolean;
  /** scopeFilter(actor, 'sales.view'): 'all' ⇒ all, > 1 người ⇒ team, còn lại self. */
  scopeKind: 'self' | 'team' | 'all';
  scopeSize: number;
}
export type DashboardAttendance = { kind: 'self'; today: MyToday } | { kind: 'presence'; presence: TodayPresence };
export interface DashboardSetup {
  projectCount: number | null;
  unitCount: number | null;
  /** null = crm chưa cung cấp. */
  hasAnyCustomers: boolean | null;
  /** Có dự án hoặc có khách ⇒ ẩn nút "Hướng dẫn bắt đầu". */
  hasData: boolean;
}
export interface DashboardOverview {
  serverTime: string;
  month: string;
  viewer: DashboardViewer;
  sales: DashboardBlock<DashboardSales>;
  inbox: DashboardBlock<Inbox>;
  commission: DashboardBlock<MyCommissionSummary>;
  board: DashboardBlock<ProjectBoardSummary>;
  attendance: DashboardBlock<DashboardAttendance>;
  setup: DashboardBlock<DashboardSetup>;
}

// ---------- Quy trình vận hành (BFF gateway GET /process/summary — gateway.md Q-BFF3) ----------
/**
 * Checklist "Bắt đầu sử dụng". null = chưa xác định (module tắt, nguồn lỗi / không phản hồi, hoặc người xem không có quyền
 * xem số đó): web hiện bước ở trạng thái trung tính "Chưa xác định" và không tính vào số bước đã xong / chưa xong.
 */
export interface ProcessChecklist {
  /** hr GetHeadcount (xong khi > 1). */
  staffCount: number | null;
  projectCount: number | null;
  unitCount: number | null;
  /** crm hasAnyCustomers (cảnh báo khách của crm). */
  hasAnyCustomers: boolean | null;
  /** Cấu hình công ty đã được lưu ít nhất một lần (identity settings version > 0). */
  settingsReviewed: boolean | null;
  hasAnyTargets: boolean | null;
}
export interface ProcessSummary {
  serverTime: string;
  /** Tầng 1 "Nguồn": "{n} lead 7 ngày qua", "{n} lead quá SLA" (phạm vi người gọi). */
  source: DashboardBlock<{ createdLast7Days: number; slaOverdue: number }>;
  /** Tầng 2 "Bán hàng": "{n} căn đang trống", "{n} căn đang giữ" (toàn tenant). */
  sales: DashboardBlock<{ open: number; locked: number }>;
  /** Tầng 3 "Giao dịch": "{n} phiếu chờ duyệt". */
  deals: DashboardBlock<{ pendingCount: number }>;
  /** Tầng 4 "Hiệu suất": "{n} người đã vào ca hôm nay". */
  performance: DashboardBlock<{ checkedIn: number }>;
  /** Tầng 5 "Tài chính": "{fmtMoney} hoa hồng chờ chi toàn sàn" — chỉ người có commission.view / payout.manage / payout.approve; người khác nhận khối forbidden (web hiện số của chính họ từ /commission/me). */
  finance: DashboardBlock<{ dueTotal: number }>;
  checklist: DashboardBlock<ProcessChecklist>;
}

// ---------- Sự kiện ----------
export const EVENTS = {
  monthlyTargetsSet: 'performance.monthlyTargets.set',
  kpiInputRecalculated: 'performance.kpiInput.recalculated',
} as const;
export type EventType = (typeof EVENTS)[keyof typeof EVENTS];

/** aggregateId = month (sheet). Mỗi lần lưu phát đúng một sự kiện (T8) — nhãn nhật ký "Đặt chỉ tiêu tháng". */
export interface MonthlyTargetsSet {
  month: string;
  version: number;
  /** Chỉ dòng đổi. */
  changes: { staffId: string; from: number | null; to: number | null }[];
  entryCount: number;
  total: number;
  sheetTotal: number;
  setBy: string;
}
/** aggregateId = '<staffId>:<month>'. Gợi ý thay đổi — commission đánh dấu bản nháp kỳ chi trả lệch nguồn. */
export interface KpiInputRecalculated {
  staffId: string;
  month: string;
  gtgd: number;
  target: number;
  targetSource: TargetSource;
  previous: { gtgd: number; target: number } | null;
  revision: number;
  cause: KpiCause;
}

export interface EventPayloads {
  [EVENTS.monthlyTargetsSet]: MonthlyTargetsSet;
  [EVENTS.kpiInputRecalculated]: KpiInputRecalculated;
}
type AssertNever<T extends never> = T;
/** Lỗi biên dịch nếu có sự kiện trong EVENTS chưa khai báo payload. */
export type _AllEventsHavePayload = AssertNever<Exclude<EventType, keyof EventPayloads>>;
